import React, { useCallback, useEffect, useRef } from 'react';
import { VenueWithMenus } from 'components/SingleItem/Details';
import SidePanel from 'components/SidePanel/SidePanel';
import { AmplitudeEventCategories, AmplitudeEventProperties, logAmplitudeEvent } from 'utils/Amplitude';
import { useOperatingHours } from 'utils/useOperatingHours';
import DetailsFooter from 'components/SingleItem/DetailsFooter';
import { useNavigate, useLocation } from 'react-router-dom';
import { type DayOfWeekType, SnackBarTypeEnum } from 'types';
import { useModalContext } from 'common/Context/ModalContext';
import { cloneDeep } from 'lodash';
import { DAYS_OF_WEEK } from 'consts';
import { formatToReadableDate } from 'utils/dateTimeHelpers';
import { OperatingHours } from 'generated/graphql';
import styles from './SingleMenuHours.module.scss';
import DayOfWeek from './DayOfWeek';

const CLOSE_SIDEPANEL = 'CLOSE_SIDEPANEL';
const CANCEL_CTA = 'CANCEL_CTA';
const FROM_SINGLE_MENU_HOURS_QUERY = '?fromSingleMenuHours=true';

interface Props {
  currentVenue?: VenueWithMenus;
  menuId: string;
  doshiiMenu?: string;
}

export const SingleMenuHours: React.FC<Props> = ({ currentVenue, menuId, doshiiMenu }) => {
  const currentApp = currentVenue?.menus.find((menu) => menu.mmId === menuId)?.apps[0] || null;
  const isVirtualBrand = currentApp?.id.startsWith('virtual.');
  const venueName = isVirtualBrand ? currentApp?.brand?.name ?? '' : currentVenue?.name ?? '';
  const { openSnackBar } = useModalContext();
  const location = useLocation();
  const params = new URLSearchParams(location.search);
  const redirectTo = params.get('redirectTo');
  const pathnameWithoutHash = location.pathname.split(`/hours`)[0];
  const navigate = useNavigate();
  const { openModal } = useModalContext();
  const { menuMMHours, setMenuMMHours, submissionStatus, hasUnsavedChanges, todayClosures, submitHours } =
    useOperatingHours({
      currentVenue,
      appId: currentApp?.id,
      doshiiMenu
    });

  const menuRefs = useRef<(HTMLDivElement | null)[]>([]);

  const displaySnackBar = useCallback(
    (message: string, type: SnackBarTypeEnum): void => {
      openSnackBar(message, undefined, undefined, type, 3000);
    },
    [openSnackBar]
  );

  // Based on updates to submissionStatus, log events, show feedback messages, reset submission state and navigate.
  useEffect(() => {
    if (submissionStatus === 'SUCCESS') {
      logAmplitudeEvent({
        name: 'click update (success)',
        category: AmplitudeEventCategories.MM_SIDE_PANEL_MENU_OPERATING_HOURS
      });
      displaySnackBar('Operating hours have been updated.', SnackBarTypeEnum.SUCCESS);
      navigate(redirectTo ? `${redirectTo}${FROM_SINGLE_MENU_HOURS_QUERY}` : pathnameWithoutHash);
    } else if (submissionStatus === 'FAILURE') {
      logAmplitudeEvent({
        name: 'click update (failure)',
        category: AmplitudeEventCategories.MM_SIDE_PANEL_MENU_OPERATING_HOURS
      });
      displaySnackBar(
        'An error occurred while updating operating hours. Please try again later.',
        SnackBarTypeEnum.INFO
      );
      navigate(redirectTo ? `${redirectTo}${FROM_SINGLE_MENU_HOURS_QUERY}` : pathnameWithoutHash);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [submissionStatus]);

  const handleDayTimeChange = (dayHours: DayOfWeekType): void => {
    if (!menuMMHours) return;
    const { day, hours } = dayHours;
    const updatedMenuHours = cloneDeep(menuMMHours);
    updatedMenuHours[day] = { day, hours };
    setMenuMMHours(updatedMenuHours);
  };

  const closeModal = (source: string): void => {
    logAmplitudeEvent({
      name: 'click cancel',
      category: AmplitudeEventCategories.MM_SIDE_PANEL_MENU_OPERATING_HOURS
    });
    if (source === CANCEL_CTA) {
      setTimeout(
        () => navigate(redirectTo ? `${redirectTo}${FROM_SINGLE_MENU_HOURS_QUERY}` : pathnameWithoutHash),
        100
      );
    } else {
      setTimeout(() => navigate(pathnameWithoutHash), 100);
    }
  };

  const handleCancel = (source: string): void => {
    if (hasUnsavedChanges) {
      openModal({
        type: 'CANCEL_CONFIRM',
        callback: () => closeModal(source)
      });
    } else {
      closeModal(source);
    }
  };

  const handleUpdate = useCallback(async (): Promise<void> => {
    const dayHoursWithError = Object.values(menuMMHours || {}).find((dayHours) => dayHours.error);
    // Scroll to the error
    if (dayHoursWithError) {
      const errorRefIndex = DAYS_OF_WEEK.indexOf(dayHoursWithError.day);
      menuRefs.current[errorRefIndex]?.scrollIntoView({ behavior: 'smooth', block: 'start' });
      return;
    }
    logAmplitudeEvent({
      name: 'click update',
      category: AmplitudeEventCategories.MM_SIDE_PANEL_MENU_OPERATING_HOURS,
      properties: {
        [AmplitudeEventProperties.APP]: currentApp?.name || '',
        [AmplitudeEventProperties.MENU_NAME]: doshiiMenu || '',
        [AmplitudeEventProperties.MENU_ID]: menuId || ''
      }
    });
    submitHours();
  }, [menuMMHours, currentApp, doshiiMenu, menuId, submitHours]);

  const renderClosureMessage = (todayClosures: OperatingHours, appName?: string): JSX.Element | null => {
    const { dates } = todayClosures;
    if (dates && dates[0] && !dates[1]) {
      return (
        <div className={styles.Alert}>
          <i className="mm-error-circle" />
          <span>
            Your {appName} menus are currently closed on{' '}
            {formatToReadableDate({
              day: dates[0].day,
              month: dates[0].month,
              year: dates[0].year
            })}
          </span>
        </div>
      );
    }
    if (dates && dates[1]) {
      return (
        <div className={styles.Alert}>
          <i className="mm-error-circle" />
          <span>
            Your {appName} menus are currently closed up to{' '}
            {formatToReadableDate({
              day: dates[1].day,
              month: dates[1].month,
              year: dates[1].year
            })}
          </span>
        </div>
      );
    }
    return null;
  };

  if (!menuMMHours) {
    return null;
  }

  return (
    <div className={styles.SingleMenuHours}>
      <SidePanel
        image={currentApp?.image ? { src: currentApp.image, name: currentApp.name } : undefined}
        title={`${venueName} ${doshiiMenu ?? 'Main'} Menu hours`.trim()}
        description="Specify the different times customers will be able to order online from this menu."
        route="hours"
        closeModal={() => handleCancel(CLOSE_SIDEPANEL)}
      >
        <div className={styles.Container}>
          {todayClosures && renderClosureMessage(todayClosures, currentApp?.name)}
          {DAYS_OF_WEEK.map((day, index) => (
            <DayOfWeek
              key={day}
              dayHours={menuMMHours[day]}
              onChange={handleDayTimeChange}
              ref={(el) => (menuRefs.current[index] = el)}
            />
          ))}
        </div>
        <div className={styles.Footer}>
          <DetailsFooter
            closeModal={() => handleCancel(CANCEL_CTA)}
            handleConfirm={handleUpdate}
            confirmButton={`Update ${currentApp?.name}`}
            loading={submissionStatus === 'PENDING'}
          />
        </div>
      </SidePanel>
    </div>
  );
};
